import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wrench, AlertTriangle, Plus, X, Play, Check, Ban, Loader2,
  Calendar, RotateCcw, ListChecks, FileText,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'

interface Plano {
  id: number
  ativo_tipo: string
  ativo_id: number
  ativo_nome: string | null
  nome: string
  tipo: string
  descricao: string | null
  gatilho_tipo: string
  gatilho_valor: number
  auto_gerar_os: boolean
  ativo: boolean
  ultima_disparada_em: string | null
}

interface OS {
  id: number
  numero: number
  plano_id: number | null
  ativo_tipo: string
  ativo_id: number
  ativo_nome: string | null
  tipo: string
  status: string
  descricao: string | null
  data_abertura: string
  data_inicio: string | null
  data_conclusao: string | null
  executor_usuario_id: number | null
  custo: number
  resultado_observacao: string | null
  ciclos_no_momento: number | null
  reset_ciclos: boolean
}

interface Alerta {
  plano_id: number
  plano_nome: string
  ativo_tipo: string
  ativo_id: number
  ativo_nome: string | null
  tipo: string
  gatilho_tipo: string
  gatilho_valor: number
  detalhe: string
  os_aberta_id: number | null
  os_gerada_id: number | null
}

interface Maquina { id: number; codigo: string; nome: string }
interface Molde { id: number; codigo: string; descricao: string }

const STATUS_COR: Record<string, string> = {
  aberta: 'bg-amber-100 text-amber-800',
  em_execucao: 'bg-sky-100 text-sky-800',
  concluida: 'bg-emerald-100 text-emerald-800',
  cancelada: 'bg-ink-200 text-ink-700',
}

export default function ManutencaoPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'alertas' | 'os' | 'planos'>('alertas')
  const [showNovaOS, setShowNovaOS] = useState(false)
  const [showNovoPlano, setShowNovoPlano] = useState(false)
  const [concluirOS, setConcluirOS] = useState<OS | null>(null)

  const { data: alertas } = useQuery<{ total: number; alertas: Alerta[] }>({
    queryKey: ['manut-alertas'],
    queryFn: async () => (await api.get('/industrial/manutencao/alertas')).data,
    refetchInterval: 60000,
  })

  const { data: oss = [] } = useQuery<OS[]>({
    queryKey: ['manut-os'],
    queryFn: async () => (await api.get('/industrial/manutencao/os')).data,
  })

  const { data: planos = [] } = useQuery<Plano[]>({
    queryKey: ['manut-planos'],
    queryFn: async () => (await api.get('/industrial/manutencao/planos')).data,
  })

  const iniciar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/industrial/manutencao/os/${id}/iniciar`)).data,
    onSuccess: () => { toast.success('OS iniciada'); qc.invalidateQueries({ queryKey: ['manut-os'] }) },
  })
  const cancelar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/industrial/manutencao/os/${id}/cancelar`)).data,
    onSuccess: () => { toast.success('OS cancelada'); qc.invalidateQueries({ queryKey: ['manut-os'] }) },
  })
  const autoGerar = useMutation({
    mutationFn: async () => (await api.get('/industrial/manutencao/alertas', { params: { auto_gerar: true } })).data,
    onSuccess: (d: any) => {
      const geradas = d.alertas.filter((a: Alerta) => a.os_gerada_id).length
      toast.success(`${geradas} OS(s) auto-gerada(s)`)
      qc.invalidateQueries({ queryKey: ['manut-alertas'] })
      qc.invalidateQueries({ queryKey: ['manut-os'] })
    },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="text-brand-600" /> Manutenção
          </h1>
          <p className="text-ink-600 text-sm">
            Planos preventivos + ordens de serviço + alertas automáticos por ciclos ou prazo.
          </p>
        </div>
        <div className="flex gap-2">
          {tab === 'os' && (
            <button onClick={() => setShowNovaOS(true)} className="btn-primary">
              <Plus size={14} /> Nova OS
            </button>
          )}
          {tab === 'planos' && (
            <button onClick={() => setShowNovoPlano(true)} className="btn-primary">
              <Plus size={14} /> Novo Plano
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-2">
        {[
          { k: 'alertas', label: 'Alertas', icon: AlertTriangle, badge: alertas?.total || 0 },
          { k: 'os', label: 'OS', icon: FileText, badge: oss.filter(o => ['aberta', 'em_execucao'].includes(o.status)).length },
          { k: 'planos', label: 'Planos', icon: ListChecks, badge: planos.filter(p => p.ativo).length },
        ].map(t => {
          const I = t.icon
          return (
            <button key={t.k} onClick={() => setTab(t.k as any)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.k ? 'border-brand-600 text-brand-700 font-semibold' : 'border-transparent text-ink-600 hover:text-ink-900'
              }`}>
              <I size={14} /> {t.label}
              {t.badge > 0 && (
                <span className={`text-[10px] px-1.5 rounded ${tab === t.k ? 'bg-brand-600 text-white' : 'bg-ink-300 text-ink-800'}`}>
                  {t.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {tab === 'alertas' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-ink-600">
              Alertas são calculados on-demand comparando ciclos atuais ou dias desde última manutenção com o plano.
            </div>
            {alertas && alertas.total > 0 && (
              <button onClick={() => autoGerar.mutate()} disabled={autoGerar.isPending} className="btn-secondary text-xs">
                {autoGerar.isPending && <Loader2 size={12} className="animate-spin" />}
                Auto-gerar OS (planos com auto-gerar)
              </button>
            )}
          </div>
          {(!alertas || alertas.total === 0) ? (
            <div className="card card-body text-center py-8 text-ink-500">
              Nenhum alerta pendente. Tudo em dia.
            </div>
          ) : (
            <div className="space-y-2">
              {alertas.alertas.map(a => (
                <div key={a.plano_id} className="card card-body border-l-4 border-amber-500 bg-amber-50">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-amber-600 shrink-0" size={20} />
                    <div className="flex-1">
                      <div className="font-semibold">{a.plano_nome}</div>
                      <div className="text-sm text-ink-700">
                        {a.ativo_tipo} · {a.ativo_nome} · <strong>{a.detalhe}</strong>
                      </div>
                      <div className="text-xs text-ink-500 mt-1">
                        Plano tipo {a.tipo} · gatilho {a.gatilho_tipo} {a.gatilho_valor}
                      </div>
                    </div>
                    <div>
                      {a.os_aberta_id ? (
                        <span className="text-xs bg-sky-100 text-sky-800 px-2 py-1 rounded">
                          OS aberta #{a.os_aberta_id}
                        </span>
                      ) : (
                        <button onClick={() => {
                          api.post('/industrial/manutencao/os', {
                            plano_id: a.plano_id, ativo_tipo: a.ativo_tipo,
                            ativo_id: a.ativo_id, tipo: a.tipo,
                            descricao: `Do plano '${a.plano_nome}': ${a.detalhe}`,
                          }).then(() => {
                            toast.success('OS criada')
                            qc.invalidateQueries({ queryKey: ['manut-alertas'] })
                            qc.invalidateQueries({ queryKey: ['manut-os'] })
                          })
                        }} className="btn-primary !py-1 text-xs">
                          <Plus size={12} /> Gerar OS
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'os' && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>#</th><th>Ativo</th><th>Tipo</th><th>Descrição</th>
                <th>Abertura</th><th>Conclusão</th><th className="text-right">Custo</th>
                <th>Status</th><th className="w-36"></th></tr>
            </thead>
            <tbody>
              {oss.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-ink-500">Nenhuma OS.</td></tr>}
              {oss.map(o => (
                <tr key={o.id}>
                  <td className="font-mono">#{o.numero}</td>
                  <td>
                    <div className="text-xs uppercase text-ink-500">{o.ativo_tipo}</div>
                    <div>{o.ativo_nome || `#${o.ativo_id}`}</div>
                  </td>
                  <td className="capitalize">{o.tipo}</td>
                  <td className="max-w-xs"><div className="truncate text-sm">{o.descricao}</div></td>
                  <td className="text-xs">{formatDateTime(o.data_abertura)}</td>
                  <td className="text-xs">{formatDateTime(o.data_conclusao)}</td>
                  <td className="text-right">{formatMoney(o.custo)}</td>
                  <td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COR[o.status]}`}>{o.status.replace('_', ' ')}</span></td>
                  <td>
                    <div className="flex gap-1">
                      {o.status === 'aberta' && (
                        <button onClick={() => iniciar.mutate(o.id)} className="btn-secondary !py-1 !px-2 text-xs text-sky-700">
                          <Play size={12} /> Iniciar
                        </button>
                      )}
                      {(o.status === 'aberta' || o.status === 'em_execucao') && (
                        <>
                          <button onClick={() => setConcluirOS(o)} className="btn-secondary !py-1 !px-2 text-xs text-emerald-700">
                            <Check size={12} /> Concluir
                          </button>
                          <button onClick={() => cancelar.mutate(o.id)} className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                            <Ban size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'planos' && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Nome</th><th>Ativo</th><th>Tipo</th><th>Gatilho</th>
                <th>Auto-OS</th><th>Última</th><th>Status</th></tr>
            </thead>
            <tbody>
              {planos.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-ink-500">Nenhum plano cadastrado.</td></tr>}
              {planos.map(p => (
                <tr key={p.id}>
                  <td className="font-semibold">{p.nome}</td>
                  <td>
                    <div className="text-xs uppercase text-ink-500">{p.ativo_tipo}</div>
                    <div>{p.ativo_nome || `#${p.ativo_id}`}</div>
                  </td>
                  <td className="capitalize">{p.tipo}</td>
                  <td className="text-xs">
                    {p.gatilho_tipo === 'ciclos' && <>a cada {p.gatilho_valor} ciclos</>}
                    {p.gatilho_tipo === 'dias' && <>a cada {p.gatilho_valor} dias</>}
                    {p.gatilho_tipo === 'manual' && 'manual'}
                  </td>
                  <td>{p.auto_gerar_os ? '✓' : '—'}</td>
                  <td className="text-xs">{formatDateTime(p.ultima_disparada_em)}</td>
                  <td>{p.ativo ? <span className="text-emerald-700 text-xs">ativo</span> : <span className="text-ink-500 text-xs">inativo</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNovaOS && <NovaOSModal onClose={() => setShowNovaOS(false)} onDone={() => {
        setShowNovaOS(false); qc.invalidateQueries({ queryKey: ['manut-os'] })
      }} />}
      {showNovoPlano && <NovoPlanoModal onClose={() => setShowNovoPlano(false)} onDone={() => {
        setShowNovoPlano(false); qc.invalidateQueries({ queryKey: ['manut-planos'] })
      }} />}
      {concluirOS && <ConcluirOSModal os={concluirOS} onClose={() => setConcluirOS(null)} onDone={() => {
        setConcluirOS(null); qc.invalidateQueries({ queryKey: ['manut-os'] })
      }} />}
    </div>
  )
}

function NovaOSModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [ativoTipo, setAtivoTipo] = useState<'maquina' | 'molde'>('maquina')
  const [ativoId, setAtivoId] = useState<number | ''>('')
  const [tipo, setTipo] = useState('corretiva')
  const [descricao, setDescricao] = useState('')

  const { data: maquinas = [] } = useQuery<Maquina[]>({
    queryKey: ['maq-manut'], queryFn: async () => (await api.get('/industrial/maquinas')).data,
  })
  const { data: moldes = [] } = useQuery<Molde[]>({
    queryKey: ['mold-manut'], queryFn: async () => (await api.get('/industrial/moldes')).data,
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/industrial/manutencao/os', {
      ativo_tipo: ativoTipo, ativo_id: ativoId, tipo, descricao: descricao || null,
    })).data,
    onSuccess: (d: OS) => { toast.success(`OS #${d.numero} criada`); onDone() },
  })

  return (
    <Modal title="Nova OS de manutenção" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo de ativo">
            <select className="input w-full" value={ativoTipo} onChange={e => { setAtivoTipo(e.target.value as any); setAtivoId('') }}>
              <option value="maquina">Máquina</option>
              <option value="molde">Molde</option>
            </select>
          </Field>
          <Field label="Ativo *">
            <select className="input w-full" value={ativoId} onChange={e => setAtivoId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— selecione —</option>
              {ativoTipo === 'maquina'
                ? maquinas.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)
                : moldes.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.descricao}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Tipo">
          <select className="input w-full" value={tipo} onChange={e => setTipo(e.target.value)}>
            <option value="corretiva">Corretiva (quebrou, precisa reparar)</option>
            <option value="preventiva">Preventiva</option>
            <option value="calibracao">Calibração</option>
            <option value="limpeza">Limpeza</option>
            <option value="troca_parte">Troca de peça</option>
          </select>
        </Field>
        <Field label="Descrição">
          <textarea className="input w-full" rows={3} value={descricao} onChange={e => setDescricao(e.target.value)} />
        </Field>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button disabled={!ativoId || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
          {criar.isPending && <Loader2 size={14} className="animate-spin" />}
          Abrir OS
        </button>
      </ModalFooter>
    </Modal>
  )
}

function NovoPlanoModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [ativoTipo, setAtivoTipo] = useState<'maquina' | 'molde'>('molde')
  const [ativoId, setAtivoId] = useState<number | ''>('')
  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState('preventiva')
  const [descricao, setDescricao] = useState('')
  const [gatilhoTipo, setGatilhoTipo] = useState('ciclos')
  const [gatilhoValor, setGatilhoValor] = useState('1000')
  const [autoGerar, setAutoGerar] = useState(true)

  const { data: maquinas = [] } = useQuery<Maquina[]>({
    queryKey: ['maq-plano'], queryFn: async () => (await api.get('/industrial/maquinas')).data,
  })
  const { data: moldes = [] } = useQuery<Molde[]>({
    queryKey: ['mold-plano'], queryFn: async () => (await api.get('/industrial/moldes')).data,
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/industrial/manutencao/planos', {
      ativo_tipo: ativoTipo, ativo_id: ativoId, nome, tipo, descricao: descricao || null,
      gatilho_tipo: gatilhoTipo, gatilho_valor: parseInt(gatilhoValor) || 0,
      auto_gerar_os: autoGerar, ativo: true,
    })).data,
    onSuccess: () => { toast.success('Plano criado'); onDone() },
  })

  return (
    <Modal title="Novo plano de manutenção" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo de ativo">
            <select className="input w-full" value={ativoTipo} onChange={e => { setAtivoTipo(e.target.value as any); setAtivoId('') }}>
              <option value="molde">Molde</option>
              <option value="maquina">Máquina</option>
            </select>
          </Field>
          <Field label="Ativo *">
            <select className="input w-full" value={ativoId} onChange={e => setAtivoId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— selecione —</option>
              {ativoTipo === 'maquina'
                ? maquinas.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)
                : moldes.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.descricao}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Nome *">
          <input className="input w-full" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Limpeza MB5 a cada 1000 ciclos" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tipo">
            <select className="input w-full" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="preventiva">Preventiva</option>
              <option value="calibracao">Calibração</option>
              <option value="limpeza">Limpeza</option>
              <option value="troca_parte">Troca de peça</option>
            </select>
          </Field>
          <Field label="Gatilho *">
            <select className="input w-full" value={gatilhoTipo} onChange={e => setGatilhoTipo(e.target.value)}>
              <option value="ciclos">Por ciclos (só moldes)</option>
              <option value="dias">Por dias</option>
              <option value="manual">Manual</option>
            </select>
          </Field>
          <Field label="Valor">
            <input type="number" className="input w-full" value={gatilhoValor}
              onChange={e => setGatilhoValor(e.target.value)}
              disabled={gatilhoTipo === 'manual'} />
          </Field>
        </div>
        <Field label="Descrição">
          <textarea className="input w-full" rows={2} value={descricao} onChange={e => setDescricao(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={autoGerar} onChange={e => setAutoGerar(e.target.checked)} />
          Auto-gerar OS quando gatilho disparar
        </label>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button disabled={!nome || !ativoId || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
          {criar.isPending && <Loader2 size={14} className="animate-spin" />}
          Criar plano
        </button>
      </ModalFooter>
    </Modal>
  )
}

function ConcluirOSModal({ os, onClose, onDone }: { os: OS; onClose: () => void; onDone: () => void }) {
  const [custo, setCusto] = useState('0')
  const [observacao, setObservacao] = useState('')
  const [resetCiclos, setResetCiclos] = useState(os.ativo_tipo === 'molde')

  const concluir = useMutation({
    mutationFn: async () => (await api.post(`/industrial/manutencao/os/${os.id}/concluir`, {
      custo: parseFloat(custo) || 0,
      resultado_observacao: observacao || null,
      reset_ciclos: resetCiclos,
    })).data,
    onSuccess: () => { toast.success('OS concluída'); onDone() },
  })
  return (
    <Modal title={`Concluir OS #${os.numero}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="text-sm text-ink-600">
          Ativo: <strong>{os.ativo_tipo}</strong> #{os.ativo_id} — {os.ativo_nome}
        </div>
        <Field label="Custo (R$)">
          <input type="number" step="0.01" className="input w-full" value={custo} onChange={e => setCusto(e.target.value)} />
        </Field>
        <Field label="Resultado / Observação">
          <textarea className="input w-full" rows={3} value={observacao} onChange={e => setObservacao(e.target.value)}
            placeholder="O que foi feito, peças trocadas, próximos passos..." />
        </Field>
        {os.ativo_tipo === 'molde' && (
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-brand-50 p-2 rounded">
            <input type="checkbox" checked={resetCiclos} onChange={e => setResetCiclos(e.target.checked)} />
            <RotateCcw size={14} />
            Resetar contador de ciclos do molde (manutenção completa)
          </label>
        )}
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button disabled={concluir.isPending} onClick={() => concluir.mutate()} className="btn-primary">
          {concluir.isPending && <Loader2 size={14} className="animate-spin" />}
          Concluir
        </button>
      </ModalFooter>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

function ModalFooter({ children }: { children: React.ReactNode }) {
  return <div className="border-t px-5 py-3 flex justify-end gap-2 mt-2 -mx-5 -mb-4 bg-ink-50">{children}</div>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium mb-1">{label}</div>
      {children}
    </label>
  )
}
